import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';


interface ICarriersContext {
    recordCount: number
    search: ISearch
    carriers: ICarrier[]
    isLoading: boolean

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchCarriers: (searchRequest: ISearch) => Promise<void>
    deleteCarrier: (id: string) => Promise<void>
}

export let carriersContext = React.createContext<ICarriersContext>({} as ICarriersContext);

let { Provider } = carriersContext;

interface ICarrier {
    id: string
    carrierCode: string
    trackingUrl: string
}

export interface ISearch {
    fullText: string
    pageNr: number
    pageSize: number
}

export let CarriersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);


    let [carriers, setCarriers] = useState<ICarrier[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchCarriers({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchCarriers = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Carrier/SearchCarriers', searchRequest);

            setCarriers(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let deleteCarrier = async (id: string) => {
        if (window.confirm("Are you sure you want to delete the carrier?")) {
            try {
                await axios.delete(`/api/Carrier/DeleteCarrier/${id}`)
                setCarriers(carriers.filter(carrier => carrier.id !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Carrier deleted", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            carriers,
            isLoading,

            doSearch,
            handleChangePage,
            updateSearch,
            searchCarriers,
            deleteCarrier
        }}>
            {children}
        </Provider>
    );
};